import { getInstance } from './searchsync-create-instance';
import { configure } from 'instantsearch.js/es/widgets';

// In-memory selection per widget container (by widgetID)
const selectedByWidget = new Map();
// Dedicated Configure widget per widgetID so we can add/remove it deterministically
const configureWidgetByWidgetId = new Map();
// Track the current pseudo filter string per widget (so we can reason about it if needed)
const currentPseudoFilterByWidgetId = new Map();

// Utility: normalize to array
const normalizeToArray = (val) => {
  if (val == null) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'object') return Object.values(val);
  return [val];
};

// Build objectID with prefix
const toObjectId = (s) => {
  const str = String(s);
  return str.startsWith('sfwd-courses#') ? str : `sfwd-courses#${str}`;
};

// Build filters string for objectIDs
const buildObjectIdFilter = (objectIds) => {
  const esc = (s) => s.replace(/"/g, '\\"');
  return objectIds.map(oid => `objectID:"${esc(oid)}"`).join(' OR ');
};

// Apply/remove our dedicated Configure widget for a given container + statuses
function applyPseudoFilterForContainer(container, statuses) {
  const widgetID = container.getAttribute('data-id');
  const indexName = container.getAttribute('data-index');
  if (!widgetID || !indexName) return;

  const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded);
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);
  const widgetData = tgblAlgoliaData[widgetID];
  const userEnrollmentStatuses = widgetData?.user_enrollment_statuses || {};

  // Collect IDs for selected statuses
  const rawIds = [];
  statuses.forEach((status) => {
    normalizeToArray(userEnrollmentStatuses[status]).forEach(v => rawIds.push(v));
  });

  const cleaned = rawIds
    .map(v => (v == null ? '' : String(v).trim()))
    .filter(s => s && s !== '[object Object]');

  const uniqueObjectIds = Array.from(new Set(cleaned.map(toObjectId)));

  // Build desired filters
  let desiredFilters;
  if (statuses.length === 0) {
    desiredFilters = null;
  } else if (uniqueObjectIds.length === 0) {
    desiredFilters = `objectID:"____NO_MATCH_7f9e8c2d____"`;
  } else {
    desiredFilters = buildObjectIdFilter(uniqueObjectIds);
  }

  const search = getInstance(indexName);
  if (!search) return;

  // Remove any previous dedicated Configure widget
  const existing = configureWidgetByWidgetId.get(widgetID);
  if (existing) {
    try {
      search.removeWidgets([existing]);
    } catch (_) {}
    configureWidgetByWidgetId.delete(widgetID);
    currentPseudoFilterByWidgetId.delete(widgetID);
  }

  // Reset page whenever our pseudo filter changes
  const uiState = search.getUiState();
  const nextIndexState = {
    ...(uiState[indexName] || {}),
    page: 1, // UI state is 1-based
  };

  if (desiredFilters === null) {
    search.setUiState({
      ...uiState,
      [indexName]: nextIndexState,
    });
    return;
  }

  const pseudoConfigure = configure({ filters: desiredFilters });
  search.addWidgets([pseudoConfigure]);
  configureWidgetByWidgetId.set(widgetID, pseudoConfigure);
  currentPseudoFilterByWidgetId.set(widgetID, desiredFilters);

  search.setUiState({
    ...uiState,
    [indexName]: nextIndexState,
  });
}

// Probe availability against the full result set (ignores InfiniteHits pagination)
// For each status: compute nbHits for (other refinements) AND (this status alone)
// IMPORTANT: We explicitly remove our pseudo filter from the probe so we behave disjunctively
async function computeAvailability({ helper, container, userEnrollmentStatuses }) {
  const indexName = container.getAttribute('data-index');
  if (!indexName || !helper) return {};

  const checkboxes = Array.from(container.querySelectorAll('.tgbl-refinement-item__checkbox'));
  const statuses = checkboxes.map(cb => String(cb.value));

  const probes = statuses.map(async (status) => {
    const raw = normalizeToArray(userEnrollmentStatuses[status]);
    const ids = Array.from(new Set(raw.map(toObjectId)));

    if (ids.length === 0) return [status, 0];

    const statusFilter = buildObjectIdFilter(ids);

    // Build a probe state starting from current helper.state BUT explicitly removing any string filters.
    // This drops our pseudo Configure filter while keeping facetFilters, numericFilters, query, etc.
    const baseState = helper.state.setQueryParameters({
      filters: undefined, // remove any string filter (e.g., our pseudo filter)
      page: 0,
      hitsPerPage: 1,
      analytics: false,
      clickAnalytics: false,
      attributesToRetrieve: ['objectID'],
    });

    // Apply only the candidate status as a string filter, disjunctively within the pseudo facet semantics
    const probeState = baseState.setQueryParameters({
      filters: statusFilter,
    });

    try {
      const { content } = await helper.searchOnce({ ...probeState });
      const nbHits = content && typeof content.nbHits === 'number' ? content.nbHits : 0;
      return [status, nbHits];
    } catch (_) {
      // Fail-open on transient errors
      return [status, 1];
    }
  });

  const results = await Promise.all(probes);
  const availability = {};
  results.forEach(([status, nbHits]) => {
    availability[status] = nbHits > 0;
  });
  return availability;
}

// Custom availability widget per container: probes every render and hides invalid statuses
function createAvailabilityWidget(container) {
  const widgetID = container.getAttribute('data-id');

  return {
    async render({ helper }) {
      if (!helper) return;

      const tgblAlgoliaDataJSON = window.atob(tgblSearchsyncDataEncoded);
      const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);
      const widgetData = tgblAlgoliaData[widgetID];
      const userEnrollmentStatuses = widgetData?.user_enrollment_statuses || {};

      const availability = await computeAvailability({
        helper,
        container,
        userEnrollmentStatuses,
      });

      // If our pseudo filter is no longer active in the search state (e.g. clear button was clicked),
      // reset our selection and uncheck all checkboxes.
      const currentFilters = helper.state.filters;
      const ourFilter = currentPseudoFilterByWidgetId.get(widgetID);
      if (ourFilter && currentFilters !== ourFilter) {
        selectedByWidget.set(widgetID, new Set());
        configureWidgetByWidgetId.delete(widgetID);
        currentPseudoFilterByWidgetId.delete(widgetID);
      }

      const selected = selectedByWidget.get(widgetID) || new Set();
      const checkboxes = Array.from(container.querySelectorAll('.tgbl-refinement-item__checkbox'));

      checkboxes.forEach(cb => {
        const status = String(cb.value);
        const isAvailable = !!availability[status];
        const isSelected = selected.has(status);

        cb.checked = isSelected;
        const row = cb.closest('.tgbl-refinement-item') || cb.parentElement;
        if (row) {
          row.classList.toggle('is-unavailable', !isAvailable);
          row.classList.toggle('ais-RefinementList-item--selected', isSelected);
        }
      });
    },
  };
}

// Init: prime selection, wire listeners, mount availability widget(s)
function initEnrollmentStatusRefinementList() {
  // Prime selected states from current DOM snapshot (once)
  document
    .querySelectorAll("[data-filter-type='enrollmentStatusRefinementList']")
    .forEach(container => {
      const widgetID = container.getAttribute('data-id');
      if (!widgetID) return;
      const preset = Array.from(container.querySelectorAll('.tgbl-refinement-item__checkbox'))
        .filter(cb => cb.checked)
        .map(cb => String(cb.value));
      selectedByWidget.set(widgetID, new Set(preset));
    });

  // Attach checkbox listeners
  document
    .querySelectorAll("[data-filter-type='enrollmentStatusRefinementList'] .tgbl-refinement-item__checkbox")
    .forEach((checkbox) => {
      checkbox.addEventListener('change', (event) => {
        // Toggle selected class on parent item (mirrors InstantSearch behavior)
        const item = event.currentTarget.closest('.tgbl-refinement-item');
        if (item) {
          item.classList.toggle('ais-RefinementList-item--selected', event.currentTarget.checked);
        }
        updateFiltersFromCheckboxes(event);
      });
    });

  // Mount availability widgets so we can hide invalid statuses per render
  document
    .querySelectorAll("[data-filter-type='enrollmentStatusRefinementList']")
    .forEach(container => {
      const indexName = container.getAttribute('data-index');
      const search = getInstance(indexName);
      if (!search) return;
      const availabilityWidget = createAvailabilityWidget(container);
      search.addWidgets([availabilityWidget]);
    });
}

export { initEnrollmentStatusRefinementList }

// Checkbox change handler: update selection and apply our pseudo filter
const updateFiltersFromCheckboxes = (event) => {
  console.log('[EnrollmentStatus] change fired', event?.currentTarget?.value, 'checked:', event?.currentTarget?.checked);
  const container =
    (event && event.currentTarget && event.currentTarget.closest("[data-filter-type='enrollmentStatusRefinementList']")) ||
    document.querySelector("[data-filter-type='enrollmentStatusRefinementList']");
  if (!container) return;

  const widgetID = container.getAttribute('data-id');
  if (!widgetID) return;

  let selected = selectedByWidget.get(widgetID);
  if (!selected) {
    selected = new Set();
    selectedByWidget.set(widgetID, selected);
  }

  if (event && event.currentTarget && event.currentTarget.type === 'checkbox') {
    const val = String(event.currentTarget.value);
    const isChecked = !!event.currentTarget.checked;
    if (isChecked) selected.add(val);
    else selected.delete(val);
  } else {
    const snapshot = Array.from(container.querySelectorAll('.tgbl-refinement-item__checkbox'))
      .filter(cb => cb.checked)
      .map(cb => String(cb.value));
    selected = new Set(snapshot);
    selectedByWidget.set(widgetID, selected);
  }

  const statuses = Array.from(selected);
  applyPseudoFilterForContainer(container, statuses);
};
